import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { PagedResults, PagingParams } from '@abaco/web-common/src/Paging';
import { ActivityProvider, ProtocolActivityResourceOut } from '../models/Activity';
import { FertilizerType, FertilizerOut, Fertilizer, FertilizerActionOut, FertilizerAction, FertilizerTypeCustom, FertilizerTypeDetailCustom } from '../models/Fertilizer';
import { copyUnitMeasure } from '../models/Product';
import { toHA } from '../models/Utils';

export default class FertilizerApi {

    private http: HTTPClient;
    private rootPath: string;

    constructor(http: HTTPClient, rootPath: string) {
        this.http = http;
        this.rootPath = rootPath
    }

    public async getFertilizerTypes(pk_cuaa: number, paging: PagingParams, filter: string | null): Promise<PagedResults<FertilizerType>> {
        const res = await this.http.get(`${this.rootPath}/v1/warehouse/subjects/${pk_cuaa}/fertilizers`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                filter: filter ? `^item_name like ${filter.toUpperCase()}` : undefined
            }
        })
        return res.data;
    }

    public async getFertilizerTypesCustom(pk_cuaa: number, filter: string | null, activityProvider: ActivityProvider, plotId: number): Promise<FertilizerTypeCustom[]> {
        let filterStr = `?subject_id=${pk_cuaa}&plot_id=${plotId}`;
        filterStr = filter ? filterStr + `&search=${filter.toUpperCase()}` : filterStr;
        const url = `v1/sync/service/${activityProvider.fertilizer_provider.service}/objects/${activityProvider.fertilizer_provider.object}/directions/${activityProvider.fertilizer_provider.direction}/preview${filterStr}`;
        const res = await this.http.get(`/sync-rest-api/api_basic/${url}`, {
            params: {
                offset: 0,
                count: 300,
            }
        })
        return res.data;
    }

    public async setFertilizerTypesCustom(pk_cuaa: number, filter: string | null, activityProvider: ActivityProvider, plotId: number, itemId: string): Promise<FertilizerTypeDetailCustom> {
        let filterStr = `?subject_id=${pk_cuaa}&plot_id=${plotId}`;
        filterStr = filter ? filterStr + `&search=${filter.toUpperCase()}` : filterStr;
        const url = `v1/sync/service/${activityProvider.fertilizer_provider.service}/objects/${activityProvider.fertilizer_provider.object}/directions/${activityProvider.fertilizer_provider.direction}/download/${itemId}${filterStr}`;
        const res = await this.http.post(`/sync-rest-api/api_basic/${url}`)
        return res.data;
    }

    public async getFertilizerType(pk_cuaa: number, item_id: number): Promise<FertilizerType> {
        const res = await this.http.get(`${this.rootPath}/v1/warehouse/subjects/${pk_cuaa}/fertilizers/${item_id}`);
        return res.data;
    }

    public async editFertilizer(plot_id: number, action_id: number, action_resource_id: number, editedFertilizer: FertilizerOut): Promise<Fertilizer> {
        const res = await this.http.put(`${this.rootPath}/v1/plots/plots/${plot_id}/activities/${action_id}/fertilizers/${action_resource_id}`, editedFertilizer);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async editProtocolFertilizer(pkCuaa: number, protocol_id: number, action_id: number, action_resource_id: number, editedFertilizer: ProtocolActivityResourceOut): Promise<Fertilizer> {
        const res = await this.http.put(`${this.rootPath}/v1/protocols/subjects/${pkCuaa}/dictionary_protocols/${protocol_id}/activities/${action_id}/fertilizers/${action_resource_id}`, editedFertilizer);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async addFertilizer(plot_id: number, action_id: number, newFertilizer: FertilizerOut): Promise<Fertilizer> {
        const res = await this.http.post(`${this.rootPath}/v1/plots/plots/${plot_id}/activities/${action_id}/fertilizers`, newFertilizer);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async addProtocolFertilizer(pkCuaa: number, protocol_id: number, action_id: number, newFertilizer: FertilizerOut): Promise<Fertilizer> {
        const res = await this.http.post(`${this.rootPath}/v1/protocols/subjects/${pkCuaa}/dictionary_protocols/${protocol_id}/activities/${action_id}/fertilizers`, newFertilizer);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async deleteFertilizer(plot_id: number, action_id: number, action_resource_id: number): Promise<Fertilizer> {
        const res = await this.http.delete(`${this.rootPath}/v1/plots/plots/${plot_id}/activities/${action_id}/fertilizers/${action_resource_id}`);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async deleteProtocolFertilizer(pkCuaa: number, protocol_id: number, action_id: number, action_resource_id: number): Promise<Fertilizer> {
        const res = await this.http.delete(`${this.rootPath}/v1/protocols/subjects/${pkCuaa}/dictionary_protocols/${protocol_id}/activities/${action_id}/fertilizers/${action_resource_id}`);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async addActionFertilizer(pk_cuaa: number, campaign: number, execution_id: number, newFertilizer: FertilizerActionOut): Promise<FertilizerAction> {
        const res = await this.http.post(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/actions/${execution_id}/fertilizers`, newFertilizer);
        toHA(res.data, ['area_covered']);
        copyUnitMeasure([res.data], "sco_unit_measure", "unit_measure")
        return res.data;
    }

    public async deleteActionFertilizer(pk_cuaa: number, campaign: number, execution_id: number, execution_resource_id: number): Promise<FertilizerAction> {
        const res = await this.http.delete(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/actions/${execution_id}/fertilizers/${execution_resource_id}`);
        return res.data;
    }

}
